/**
 * Users Service Layer
 * 
 * This module provides database operations for the users collection.
 * Used for storing user profiles with admin and approval status.
 */

import { doc, getDoc, setDoc, getDocs, updateDoc, collection, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Collection name in Firestore
const COLLECTION_NAME = 'users';

/**
 * Sends a notification to ntfy.sh when a new user signs up
 *
 * The notification title is sent as the ntfy `Title` header, which must be
 * ASCII-only; emoji are therefore kept in the (UTF-8) message body.
 *
 * @param {string} email - The new user's email
 * @param {string} [voornaam] - Optional first name
 * @param {string} [achternaam] - Optional last name
 */
const sendNotification = async (email, voornaam = '', achternaam = '') => {
  const ntfyTopic = process.env.REACT_APP_NTFY_USER_SIGNUP_TOPIC;
  if (!ntfyTopic) {
    console.warn('NTFY user-signup topic not configured, skipping notification');
    return;
  }
  const fullName = `${voornaam || ''} ${achternaam || ''}`.trim();
  const userLabel = fullName ? `${fullName} (${email})` : email;
  try {
    await fetch(`https://ntfy.sh/${ntfyTopic}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        Title: 'Nieuwe gebruiker aangemeld'
      },
      body: `🆕 ${userLabel}\n\nGa naar de app om goed te keuren of af te wijzen.`
    });
  } catch (error) {
    console.error('Error sending notification:', error);
    // Don't throw - notification failure shouldn't block signup
  }
};

/**
 * Gets a user profile from Firestore
 * 
 * @param {string} uid - The user's Firebase Auth UID
 * @returns {Promise<Object|null>} The user profile or null if not found
 */
export const getUserProfile = async (uid) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Creates or updates a user profile in Firestore
 * Called on first login to ensure user document exists
 * Sends notification to admin when new user signs up
 * 
 * @param {string} uid - The user's Firebase Auth UID
 * @param {string} email - The user's email
 * @param {Object} [profileData] - Optional profile data
 * @param {string} [profileData.voornaam] - The user's first name
 * @param {string} [profileData.achternaam] - The user's last name
 * @returns {Promise<Object>} The created/existing user profile
 */
export const createUserProfile = async (uid, email, profileData = {}) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      // User already exists, return existing profile
      return { id: userSnap.id, ...userSnap.data() };
    }
    
    // Create new user profile (pending approval)
    const newUserProfile = {
      email,
      voornaam: (profileData.voornaam || '').trim(),
      achternaam: (profileData.achternaam || '').trim(),
      isAdmin: false,
      status: 'pending',
      createdAt: serverTimestamp()
    };
    
    await setDoc(userRef, newUserProfile);
    
    // Send notification to admin
    await sendNotification(email, newUserProfile.voornaam, newUserProfile.achternaam);
    
    return {
      id: uid,
      ...newUserProfile,
      createdAt: new Date()
    };
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Updates the editable fields of a user's own profile.
 *
 * Only `voornaam`, `achternaam` and `telefoon` are accepted; all other
 * fields (including security-sensitive ones like `isAdmin`, `status` and
 * `email`) are ignored. Values are trimmed before persisting.
 *
 * @param {string} uid - The user's Firebase Auth UID
 * @param {Object} profileUpdates - Editable profile fields
 * @param {string} [profileUpdates.voornaam] - First name
 * @param {string} [profileUpdates.achternaam] - Last name
 * @param {string} [profileUpdates.telefoon] - Phone number
 * @returns {Promise<Object>} The updated profile fields with id
 */
export const updateUserProfile = async (uid, profileUpdates = {}) => {
  const EDITABLE_FIELDS = ['voornaam', 'achternaam', 'telefoon'];

  try {
    const sanitizedProfile = {};
    EDITABLE_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(profileUpdates, field)) {
        sanitizedProfile[field] =
          typeof profileUpdates[field] === 'string' ? profileUpdates[field].trim() : '';
      }
    });

    const userRef = doc(db, COLLECTION_NAME, uid);
    await updateDoc(userRef, {
      ...sanitizedProfile,
      updatedAt: serverTimestamp()
    });

    return {
      id: uid,
      ...sanitizedProfile,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Gets all users from Firestore (admin only)
 * @returns {Promise<Array>} Array of all user profiles
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, COLLECTION_NAME);
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Approves a user (sets status to 'approved')
 * @param {string} uid - The user's Firebase Auth UID
 */
export const approveUser = async (uid) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    await updateDoc(userRef, {
      status: 'approved',
      approvedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error approving user:', error);
    throw error;
  }
};

/**
 * Rejects a user (sets status to 'rejected')
 * @param {string} uid - The user's Firebase Auth UID
 */
export const rejectUser = async (uid) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    await updateDoc(userRef, {
      status: 'rejected',
      rejectedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error rejecting user:', error);
    throw error;
  }
};

/**
 * Sets admin status for a user
 * @param {string} uid - The user's Firebase Auth UID
 * @param {boolean} isAdmin - Whether the user should be admin
 */
export const setAdminStatus = async (uid, isAdmin) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    await updateDoc(userRef, { isAdmin });
  } catch (error) {
    console.error('Error setting admin status:', error);
    throw error;
  }
};
